import { moment } from 'omni-common-ui';
import { List, Record, OrderedMap } from 'immutable';
import is from 'is_js';

export function getCoveredInClassObject(state, sessionPlanId) {
  return state
    .get('rootReducer')
    .get('coveredInClassContent')
    .get(sessionPlanId);
}

export function getCoveredInClassTemplate(state, sessionPlanId) {
  return state
  .get('rootReducer')
  .get('coveredInClassTemplate')
  .get(sessionPlanId);
}

export function isAllCategoryItemsChecked(coveredInClassContent) {
  return coveredInClassContent &&
    coveredInClassContent.reduce((isCheck, coveredInClass) =>
      isCheck &&
      coveredInClass.cicCategories.every(({ cicCategoryItems }) =>
        cicCategoryItems.every(({ isChecked }) => isChecked)) &&
      coveredInClass.cicActivities.every(({ cicActivityItems }) =>
        cicActivityItems.every(({ isChecked }) => isChecked))
      , true
  );
}

export function filterUncheckItems(coveredInClassContent) {
  return coveredInClassContent.map((coveredInClass) =>
      coveredInClass.update('cicActivities', (cicActivities) =>
        cicActivities.map((cicActivity) => cicActivity.update('cicActivityItems', (cicActivityItems) =>
          cicActivityItems.filter((cicActivityItem) => cicActivityItem.isChecked))))
      .update('cicCategories', (cicCategories) =>
        cicCategories.map((cicCategory) => cicCategory.update('cicCategoryItems', (cicCategoryItems) =>
          cicCategoryItems.filter((cicCategoryItem) => cicCategoryItem.isChecked)))));
}

export function isAllCategoryItemsUnchecked(coveredInClassContent) {
  if (! coveredInClassContent || coveredInClassContent.size === 0) return true;
  return coveredInClassContent.reduce((isCheck, coveredInClass) =>
    isCheck &&
    coveredInClass.cicCategories.every(({ cicCategoryItems }) =>
      cicCategoryItems.every(({ isChecked }) => ! isChecked)) &&
    coveredInClass.cicActivities.every(({ cicActivityItems }) =>
      cicActivityItems.every(({ isChecked }) => ! isChecked))
    , true
  );
}

export function toggleCategoryItemsCheckedState(coveredInClassContent) {
  if (! coveredInClassContent) {
    return;
  }
  const newIsChecked = ! isAllCategoryItemsChecked(coveredInClassContent);
  return coveredInClassContent.map((coveredInClass) =>
    coveredInClass.update('cicCategories', (cicCategories) => cicCategories.map((cicCategory) =>
      cicCategory.update('cicCategoryItems', (cicCategoryItems) =>
        cicCategoryItems.map((cicCategoryItem) => cicCategoryItem.set('isChecked', newIsChecked))
      )))
      .update('cicActivities', (cicActivities) => cicActivities.map((cicActivity) =>
        cicActivity.update('cicActivityItems', (cicActivityItems) =>
          cicActivityItems.map((cicActivityItem) => cicActivityItem.set('isChecked', newIsChecked))
        )))
  );
}

export function getCurrentSessionNumber(state, { params: { groupId, sessionPlanId } }) {
  let group = state.get('rootReducer').get('groups').get(groupId);
  group = group && group.data;
  return group && group.sessionPlans.data && group.sessionPlans.data.indexOf(sessionPlanId) + 1;
}

export function hasWronglyResult(state, sessionPlanId) {
  const result = getCoveredInClassObject(state, sessionPlanId);
  const template = getCoveredInClassTemplate(state, sessionPlanId);
  if (is.existy(result) && is.existy(template)) {
    if (result.size !== template.size) {
      return true;
    }
    return template.size !== template.filter((t) =>
      result.some((r) => r.unitId === t.unitId && r.lessonNumber === t.lessonNumber)).size;
  }

  return undefined;
}

export function isNotFutureSession(state, params) {
  const currentSessionDate = getCurrentSessionDate(state, params);
  return moment().isAfter(currentSessionDate);
}

export function hasSaved(state, sessionPlanId) {
  const result = getCoveredInClassObject(state, sessionPlanId);
  const template = getCoveredInClassTemplate(state, sessionPlanId);
  if (is.existy(template) && is.existy(result)) {
    return result.size !== 0;
  }
  return undefined;
}

export function isNotPublished(state, { sessionPlanId }) {
  const coveredInClassObject = getCoveredInClassObject(state, sessionPlanId);
  return ! coveredInClassObject ||
      coveredInClassObject.size === 0 ||
      coveredInClassObject.some((cic) => cic && cic.cicStatus !== 'Published');
}

export function hasCIC(state, { sessionPlanId }) {
  const coveredInClassObject = getCoveredInClassObject(state, sessionPlanId);
  return hasCICItems(coveredInClassObject);
}

export function hasCICTemplate(state, { sessionPlanId }) {
  const coveredInClassTemplate = getCoveredInClassTemplate(state, sessionPlanId);
  return hasCICItems(coveredInClassTemplate);
}

function hasCICItems(coveredInClass) {
  return coveredInClass && coveredInClass.some((cic) =>
  (cic && cic.cicActivities && cic.cicActivities.size > 0) ||
  (cic && cic.cicCategories && cic.cicCategories.size > 0));
}

export function getCurrentSessionDate(state, { groupId, sessionPlanId }) {
  const group = state.get('rootReducer').get('groups').get(groupId).data;
  const { sessionPlans: sessionPlanIds } = group;
  const currentSessionPlanId = sessionPlanId;
  const sessionPlansInfo = state.get('rootReducer').get('sessionPlans');
  const sessionPlans = sessionPlanIds.update('data', (data) => new List(data))
    .update('data', (data) =>
      data.map((planId) => sessionPlansInfo.get(planId)));
  const currentSessionIndex = sessionPlans.data.findIndex((plan) =>
    plan.id === currentSessionPlanId
  );
  const currentSession = sessionPlans.data.get(currentSessionIndex);
  const currentSessionDate = currentSession.period.start;
  return currentSessionDate;
}

export function mergeCategoriesByName(categories) {
  let mergedCategories = new List();
  const newCategories = categories.map((category) => {
    const newCicCategoryItems = category.cicCategoryItems.map((cicCategoryItem) => Record({
      cicCategoryItemId: undefined,
      cicCategoryItemName: undefined,
      isChecked: undefined,
      cicCategoryId: undefined,
    })(Object.assign({}, cicCategoryItem.toJS(), { cicCategoryId: category.cicCategoryId })));

    return Record({
      cicCategoryName: undefined,
      cicCategoryItems: undefined,
      cicCategoryId: undefined,
    })({
      cicCategoryName: category.cicCategoryName,
      cicCategoryItems: newCicCategoryItems,
      cicCategoryId: category.cicCategoryId,
    });
  });

  newCategories.forEach((newCategory) => {
    let existingCategory = mergedCategories.findEntry(_isSameCategory);
    existingCategory = existingCategory && existingCategory[1];
    if (! existingCategory) {
      mergedCategories = mergedCategories.push(newCategory);
    } else {
      mergedCategories = mergedCategories.map((mergedCategory) => {
        if (mergedCategory.cicCategoryName === existingCategory.cicCategoryName) {
          return mergedCategory.update('cicCategoryItems', _mergedCategoryItems);
        }
        return mergedCategory;
      });
    }

    function _mergedCategoryItems(cicCategoryItems) {
      return cicCategoryItems.concat(newCategory.cicCategoryItems);
    }

    function _isSameCategory(mergedCategory) {
      return mergedCategory.cicCategoryName === newCategory.cicCategoryName;
    }
  });

  return mergedCategories;
}

function listToMap(list, keyField) {
  return list.reduce((map, item) => map.set(item[keyField], item), new OrderedMap({}));
}

function categoryToOrderMap(list) {
  return list.reduce((map, item) => map.set(item.cicCategoryName, item.update('cicCategoryItems', (cicCategoryItems) =>
      listToMap(cicCategoryItems, 'cicCategoryItemId'))), new OrderedMap({}));
}

function activityToOrderMap(list) {
  return list.reduce((map, item) => map.set(item.cicActivityId, item.update('cicActivityItems', (cicActivityItems) =>
      listToMap(cicActivityItems, 'cicActivityItemId'))), new OrderedMap({}));
}

export function categoryDeepMerge(template, categories) {
  const templateMap = categoryToOrderMap(template);
  const categoriesMap = categoryToOrderMap(categories);
  const result = templateMap.mergeDeep(categoriesMap);
  return result.map((item) => item.update('cicCategoryItems', (map) => map.toList()));
}

export function activityDeepMerge(template, activities) {
  const templateMap = activityToOrderMap(template);
  const activitiesMap = activityToOrderMap(activities);
  const result = templateMap.mergeDeep(activitiesMap);
  return result.map((item) => item.update('cicActivityItems', (map) => map.toList()));
}




// WEBPACK FOOTER //
// ./data/CoveredInClass/accessors.js